import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Sparkles, Copy, Check } from 'lucide-react';
import { api } from '../../lib/api';

export function AIAssistantPage() {
  const [brand, setBrand] = useState('');
  const [category, setCategory] = useState('');
  const [size, setSize] = useState('');
  const [colour, setColour] = useState('');
  const [condition, setCondition] = useState('excellent');
  const [notes, setNotes] = useState('');

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [copied, setCopied] = useState<string | null>(null);

  const [aiError, setAiError] = useState('');

  const generateTitle = useMutation({
    mutationFn: () => api.post('/ai/generate-title', { brand, category, size, colour, condition, notes }).then((r) => r.data),
    onSuccess: (d) => { setTitle(d.title ?? ''); setAiError(''); },
    onError: (e: any) => setAiError(e?.response?.data?.error ?? 'AI request failed. Check GEMINI_API_KEY is set in Railway.'),
  });

  const generateDescription = useMutation({
    mutationFn: () => api.post('/ai/generate-description', { title, brand, size, condition, notes }).then((r) => r.data),
    onSuccess: (d) => { setDescription(d.description ?? ''); setAiError(''); },
    onError: (e: any) => setAiError(e?.response?.data?.error ?? 'AI request failed. Check GEMINI_API_KEY is set in Railway.'),
  });

  const generateTags = useMutation({
    mutationFn: () => api.post('/ai/generate-tags', { title, brand, category }).then((r) => r.data),
    onSuccess: (d) => { setTags(d.tags ?? []); setAiError(''); },
    onError: (e: any) => setAiError(e?.response?.data?.error ?? 'AI request failed. Check GEMINI_API_KEY is set in Railway.'),
  });

  function copy(value: string, key: string) {
    navigator.clipboard.writeText(value);
    setCopied(key);
    setTimeout(() => setCopied(null), 1200);
  }

  return (
    <div className="flex flex-col gap-3.5">
      <div>
        <h1 className="text-base font-semibold tracking-tight flex items-center gap-2">
          <Sparkles size={16} className="text-secondary-400" /> AI assistant
        </h1>
        <p className="text-[11.5px] text-white/25 mt-0.5">Generate listing copy, tags and pricing from a few details</p>
      </div>

      {aiError && (
        <div className="text-[12px] text-danger bg-danger/10 border border-danger/20 rounded-lg px-4 py-3">
          {aiError}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
        <div className="card p-5 space-y-3">
          <h2 className="text-[13px] font-semibold mb-1">Item details</h2>
          <Field label="Brand"><input className="input" value={brand} onChange={(e) => setBrand(e.target.value)} /></Field>
          <Field label="Category"><input className="input" value={category} onChange={(e) => setCategory(e.target.value)} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Size"><input className="input" value={size} onChange={(e) => setSize(e.target.value)} /></Field>
            <Field label="Colour"><input className="input" value={colour} onChange={(e) => setColour(e.target.value)} /></Field>
          </div>
          <Field label="Condition">
            <select className="input" value={condition} onChange={(e) => setCondition(e.target.value)}>
              <option value="new_with_tags">New with tags</option>
              <option value="new_without_tags">New without tags</option>
              <option value="excellent">Excellent used</option>
              <option value="good">Good used</option>
              <option value="fair">Fair</option>
            </select>
          </Field>
          <Field label="Notes (optional)"><textarea className="input resize-none h-16" value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>
        </div>

        <div className="space-y-3.5">
          <Output
            label="Title"
            value={title}
            generating={generateTitle.isPending}
            onGenerate={() => generateTitle.mutate()}
            onCopy={() => copy(title, 'title')}
            copied={copied === 'title'}
          />
          <Output
            label="Description"
            value={description}
            generating={generateDescription.isPending}
            onGenerate={() => generateDescription.mutate()}
            onCopy={() => copy(description, 'desc')}
            copied={copied === 'desc'}
            multiline
          />
          <div className="card p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-semibold text-white/40 uppercase tracking-wider">Tags</span>
              <button onClick={() => generateTags.mutate()} className="text-[11px] text-primary-300 flex items-center gap-1">
                <Sparkles size={11} /> {generateTags.isPending ? 'Generating...' : 'Generate'}
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {tags.length > 0 ? tags.map((t) => (
                <span key={t} className="px-2 py-0.5 rounded-md bg-secondary-400/[0.08] text-secondary-400 text-[11px]">{t}</span>
              )) : (
                <div className="text-[11px] text-white/25">Tags will appear here</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div><div className="text-[11px] text-white/35 mb-1">{label}</div>{children}</div>;
}

function Output({ label, value, generating, onGenerate, onCopy, copied, multiline }: {
  label: string; value: string; generating: boolean; onGenerate: () => void; onCopy: () => void; copied: boolean; multiline?: boolean;
}) {
  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] font-semibold text-white/40 uppercase tracking-wider">{label}</span>
        <div className="flex gap-2">
          {value && <button onClick={onCopy} className="text-[11px] text-white/40 flex items-center gap-1">
            {copied ? <><Check size={11} className="text-secondary-400" /> Copied</> : <><Copy size={11} /> Copy</>}
          </button>}
          <button onClick={onGenerate} className="text-[11px] text-primary-300 flex items-center gap-1">
            <Sparkles size={11} /> {generating ? 'Generating...' : 'Generate'}
          </button>
        </div>
      </div>
      {multiline ? (
        <div className="text-[12.5px] text-white/80 leading-relaxed min-h-[60px] whitespace-pre-wrap">{value || <span className="text-white/25">Generated description will appear here</span>}</div>
      ) : (
        <div className="text-[13px] text-white/85 font-medium min-h-[20px]">{value || <span className="text-white/25 font-normal">Generated title will appear here</span>}</div>
      )}
    </div>
  );
}
